import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import Login from "@/pages/Login";
import CustomerDashboard from "@/pages/CustomerDashboard";
import CustomerTickets from "@/pages/CustomerTickets";
import CreateTicket from "@/pages/CreateTicket";
import TicketDetail from "@/pages/TicketDetail";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminTickets from "@/pages/AdminTickets";
import AdminReports from "@/pages/AdminReports";
import AdminSettings from "@/pages/AdminSettings";
import AdminUsers from "@/pages/AdminUsers";
import AdminMasterData from "@/pages/AdminMasterDataPage";
import AdminStagingPage from "@/pages/AdminStagingPage";
import ProfilePage from "@/pages/ProfilePage";

function Loading() {
  return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
}

function Protected({ children, adminOnly, superAdminOnly, masterDataOnly }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" state={{ from: location }} replace />;
  if (superAdminOnly && user.role !== "super_admin") return <Navigate to="/dashboard" replace />;
  if (masterDataOnly && !["admin", "super_admin"].includes(user.role)) return <Navigate to="/dashboard" replace />;
  if (adminOnly && !["admin", "super_admin", "technician"].includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user) return <Navigate to={["admin", "super_admin", "technician"].includes(user.role) ? "/admin" : "/dashboard"} replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />

      {/* Customer */}
      <Route path="/dashboard" element={<Protected><CustomerDashboard /></Protected>} />
      <Route path="/tickets" element={<Protected><CustomerTickets /></Protected>} />
      <Route path="/tickets/new" element={<Protected><CreateTicket /></Protected>} />
      <Route path="/tickets/:id" element={<Protected><TicketDetail /></Protected>} />
      <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />

      {/* Admin & Super Admin */}
      <Route path="/admin" element={<Protected adminOnly><AdminDashboard /></Protected>} />
      <Route path="/admin/tickets" element={<Protected adminOnly><AdminTickets /></Protected>} />
      <Route path="/admin/tickets/:id" element={<Protected adminOnly><TicketDetail /></Protected>} />
      <Route path="/admin/reports" element={<Protected adminOnly><AdminReports /></Protected>} />
      <Route path="/admin/settings" element={<Protected adminOnly><AdminSettings /></Protected>} />
      <Route path="/admin/master-data" element={<Protected masterDataOnly><AdminMasterData /></Protected>} />
      <Route path="/admin/staging" element={<Protected adminOnly><AdminStagingPage /></Protected>} />
      <Route path="/admin/profile" element={<Protected adminOnly><ProfilePage /></Protected>} />

      {/* Super Admin Only */}
      <Route path="/admin/users" element={<Protected superAdminOnly><AdminUsers /></Protected>} />

      <Route path="/" element={<Navigate to={user && ["admin", "super_admin", "technician"].includes(user.role) ? "/admin" : "/dashboard"} replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
